import { useEffect, useRef } from 'react';
import {
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  theme,
  type Message,
} from '@agentisai/design-system';
import type { WorkstreamMode } from '@agentisai/shared-types';

/**
 * The platform agents a user may invite into a workstream conversation
 * (mirrors `apps/workstreamagent/api/agents/platform/*` minus `discovery`,
 * which is the pre-creation interview agent only — see `create-chat.ts`).
 * Hand-maintained until the registry is exposed to the BFF (follow-on).
 */
export const INVITABLE_AGENTS: readonly { name: string; label: string }[] = [
  { name: 'architect', label: 'Architect' },
  { name: 'architecture-reviewer', label: 'Architecture Reviewer' },
  { name: 'delivery', label: 'Delivery' },
  { name: 'governance', label: 'Governance' },
  { name: 'legal-reviewer', label: 'Legal Reviewer' },
];

export interface WorkspaceConversationProps {
  title: string;
  messages: Message[];
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  maxLength?: number;
  pending?: boolean;
  sendError?: string | null;
  /** Currently selected invite-agent hint, or `null` for router-auto. */
  invitedAgent: string | null;
  onInviteAgent: (agentName: string | null) => void;
  /**
   * Librarian routing mode, as last reported by the server. `discovery`
   * locks the conversation to the discovery interview — the invite row is
   * replaced by the exit affordance until the user ends the interview.
   */
  mode?: WorkstreamMode;
  /** Ends the discovery interview (sends the `exitDiscovery` action). */
  onExitDiscovery?: () => void;
  /**
   * Consensus-tracker conflict detail for the latest turn, or `null` when the
   * agents agreed. Rendered as a warning banner (Librarian flow step 8b —
   * "surface conflict to user").
   */
  conflict?: string | null;
}

/**
 * WorkspaceConversation — the live conversation for an already-created
 * workstream (post-creation). Composes the same DS primitives as
 * `CreateWorkstreamPanel` (ChatPanel + MessageBubble + Composer), rendered
 * `fluid` inline in the WorkspaceShell main pane rather than in a Modal.
 * State is fully owned by the parent (HomePage) — this component renders only.
 */
export function WorkspaceConversation({
  title,
  messages,
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  sendError,
  invitedAgent,
  onInviteAgent,
  mode = 'normal',
  onExitDiscovery,
  conflict,
}: WorkspaceConversationProps) {
  // Auto-scroll to the latest turn — same recipe as CreateWorkstreamPanel
  // (imperative DOM sync, the sanctioned effect+ref use, frontend-architecture R24/R25).
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  const composerLeadingActions = (
    <>
      <IconButton
        aria-label="Attach file"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Paperclip} size={14} />}
      />
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <ChatPanel
      fluid
      caption={
        mode === 'discovery'
          ? 'Discovery interview in progress — the discovery agent leads until you exit'
          : 'Ask a question, or invite a specialist agent below'
      }
      title={title}
      awaitingReply={pending}
      messages={
        <>
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          <div ref={bottomRef} aria-hidden="true" />
        </>
      }
      composer={
        <>
          {mode === 'discovery' ? (
            // Discovery locks routing server-side — inviting is inert, so the
            // row is replaced by the one meaningful action: exit the interview.
            // If this chrome grows beyond a single button (banner, distinct
            // composer state), promote it to a DS ChatPanel variant + story (R0).
            <div
              style={{
                display: 'flex',
                gap: theme.space['1_5'],
                padding: `${theme.space[2]} ${theme.space[4]} 0`,
              }}
            >
              <Button size="sm" variant="secondary" disabled={pending} onClick={onExitDiscovery}>
                Exit Discovery Interview
              </Button>
            </div>
          ) : (
            <div
              role="group"
              aria-label="Invite an agent"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.space['1_5'],
                padding: `${theme.space[2]} ${theme.space[4]} 0`,
              }}
            >
              <Button
                size="sm"
                variant={invitedAgent === null ? 'secondary' : 'ghost'}
                aria-pressed={invitedAgent === null}
                onClick={() => onInviteAgent(null)}
              >
                Auto
              </Button>
              {INVITABLE_AGENTS.map((agent) => (
                <Button
                  key={agent.name}
                  size="sm"
                  variant={invitedAgent === agent.name ? 'secondary' : 'ghost'}
                  aria-pressed={invitedAgent === agent.name}
                  onClick={() => onInviteAgent(agent.name)}
                >
                  {agent.label}
                </Button>
              ))}
            </div>
          )}
          {conflict && (
            // Advisory, not an error: polite live region so SRs announce the
            // disagreement without interrupting (Callout carries no role itself).
            <div
              role="status"
              aria-live="polite"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="warn" title="Agents disagreed on this reply">
                {conflict}
              </Callout>
            </div>
          )}
          {sendError && (
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">{sendError}</Callout>
            </div>
          )}
          <Composer
            value={value}
            onChange={onChange}
            onSend={onSend}
            sendDisabled={sendDisabled}
            placeholder={
              mode === 'discovery'
                ? 'Reply to the discovery agent…'
                : invitedAgent
                  ? `Ask ${INVITABLE_AGENTS.find((a) => a.name === invitedAgent)?.label}…`
                  : 'Ask a question…'
            }
            leadingActions={composerLeadingActions}
          />
          {maxLength != null && (value?.length ?? 0) > maxLength && (
            <div
              role="alert"
              aria-live="assertive"
              style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
            >
              <Callout tone="danger">
                Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                {maxLength.toLocaleString()} characters. Shorten it to send.
              </Callout>
            </div>
          )}
        </>
      }
    />
  );
}
WorkspaceConversation.displayName = 'WorkspaceConversation';
